import React, {useState, useEffect} from 'react';
import axios from 'axios';
import AdminMenu from "../AdminMenu";
import AdminHeader from "../AdminHeader";
import Button from "../../../components/elements/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSave} from "@fortawesome/free-solid-svg-icons";
import InputField from "../../../components/elements/InputField";
import TextEditor from "../../../components/elements/TextEditor";
import {useNavigate, useParams} from 'react-router-dom';
import ErrorMessage from "../../../components/layout/ErrorMessage";
import ImageUpload from "../../../components/elements/ImageUpload";
import TextArea from "../../../components/elements/TextArea";
import TagsInput from "../../../components/elements/TagsInput";
import {calculateReadTime} from "../../../utils/commonUtils"
import {ADMIN_API_BASE, resolvePublicImageUrl} from "../../../utils/apiConfig";
import CustomSelect from "../../../components/elements/CustomSelect";

const AdminBlogPost = () => {
    const [blog, setBlog] = useState({
        title: '',
        h1: '',
        slug: '',
        category: '',
        meta_description: '',
        content: '',
        allow_comments: true,
        read_time: 0,
        related_posts: '',
        tags: [],
        image: '',
        status: null
    });

    const [files, setFiles] = useState([]);
    const [message, setMessage] = useState({type: '', text: ''});
    const navigate = useNavigate();
    const {id: initialPostId} = useParams();
    const [postId, setPostId] = useState(initialPostId || null);
    const [status, setStatus] = useState(null);

    const getCsrfToken = async () => {
        const response = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            method: "GET",
            credentials: "include", // critical to send the session cookie
        });

        const data = await response.json();
        return data.csrfToken;
    };

    useEffect(async () => {
            const fetchDraft = async (idToFetch) => {
                    if (!idToFetch) {
                        return;
                    }
                    try {
                        const token = await getCsrfToken();
                        const response = await axios.get(`${ADMIN_API_BASE}/blog/draft/${idToFetch}`, {
                            withCredentials: true,
                            headers: {
                                'csrf-token': token,
                            },
                        });
                        if (response.data && response.data.draft) {
                            const postData = response.data.draft;
                            // --- SAFE TAGS PARSING ---
                            let cleanTags = [];
                            if (postData.tags) {
                                if (Array.isArray(postData.tags)) {
                                    cleanTags = postData.tags;
                                } else if (typeof postData.tags === 'string') {
                                    try {
                                        // Try parsing in case it's a JSON string '["tag1", "tag2"]'
                                        cleanTags = JSON.parse(postData.tags);
                                    } catch (e) {
                                        // Fallback if it's a comma-separated string "tag1,tag2"
                                        cleanTags = postData.tags.split(',').map(t => t.trim()).filter(Boolean);
                                    }
                                }
                            }

                            const updatedBlog = {
                                ...postData,
                                tags: cleanTags
                            };

                            setBlog(updatedBlog);
                            setStatus(response.data.draft.status);
                            setPostId(idToFetch); // Ensure postId is set if fetch is successful
                        } else {
                            console.error("Draft not found or invalid response");
                            setMessage({type: 'error', text: 'Draft not found.'});
                        }
                    } catch
                        (error) {
                        console.error("Error fetching draft:", error);
                        setMessage({type: 'error', text: 'Error fetching draft: ' + error.message});
                    }
                }
            ;

            if (initialPostId) {
                fetchDraft(initialPostId); // Call fetchDraft with initialPostId
            }

        }, [postId]
    )
    ;

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/\s+/g, '-')   // Replace spaces with hyphens
            .replace(/[^\w-]+/g, '') // Remove all non-word chars
            .slice(0, 100);         // Limit length
    };

    const handleTitleChange = (e) => {
        const {name, value} = e.target;
        setBlog((prevBlog) => ({...prevBlog, [name]: value}));
        if (name === 'title' && !blog.slug) {
            setBlog((prevBlog) => ({...prevBlog, slug: generateSlug(value)}));
        }
    };

    const handleEditorContentChange = (content) => {
        const estimatedReadTime = calculateReadTime(content);
        setBlog(prevBlog => ({
            ...prevBlog,
            content,
            read_time: estimatedReadTime
        }));
    };

    const handleInputChange = (e) => {
        if (e && e.target) {
            const {name, value} = e.target;

            if (name === "tags") {
                const tagsArray = Array.isArray(value) ? value : value.split(',').map(tag => tag.trim());
                setBlog((prevBlog) => ({...prevBlog, [name]: tagsArray}));
            } else {
                setBlog((prevBlog) => ({...prevBlog, [name]: value}));
            }
        } else if (Array.isArray(e)) {
            // This handles the call: onChange(updatedTags) from your TagsInput
            setBlog((prevBlog) => ({...prevBlog, tags: e}));
        }
    };


    const handleFileUpload = (file) => {
        setFiles([file]); // Directly set the array with the received file object
    };

    const saveOrUpdateBlog = async (_status) => {

        const formData = new FormData();
        formData.append("title", blog.title);
        formData.append("h1", blog.h1);
        formData.append("slug", blog.slug);
        formData.append("category", blog.category);
        formData.append("meta_description", blog.meta_description);
        formData.append("content", blog.content);
        formData.append("allow_comments", blog.allow_comments);
        formData.append("read_time", blog.read_time);
        formData.append("related_posts", blog.related_posts);
        formData.append('tags', JSON.stringify(blog.tags || []));

        if (files.length > 0 && files[0] instanceof File) {
            formData.append("image", files[0]);
        } else {
        }

        formData.append("status", _status);

        try {
            const token = await getCsrfToken();
            let response;
            if (postId) {
                response = await axios.put(`${ADMIN_API_BASE}/blog/update-post/${postId}`, formData, {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                });
            } else {
                response = await axios.post(`${ADMIN_API_BASE}/blog/create-post`, formData, {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                });

                if (response.data.success) {
                    setMessage({type: "success", text: "blog post saved successfully!"});

                    // ✅ CRITICAL: Clear the local files state
                    // This forces ImageUpload to stop showing the 'blob:' and start showing 'existingImage'
                    setFiles([]);

                    // Refresh the blog state with the new image key from server
                    if (response.data.image) {
                        setBlog(prev => ({...prev, image: response.data.image}));
                    } else {
                        fetchUpdatedPost(postId || response.data.id);
                    }
                } else {
                    console.error("Error: No ID returned from create-post API");
                }
            }

            setMessage({type: "success", text: "blog post saved successfully!"});

            // After successful save/update, if the backend returns the new image path
            if (response && response.data && response.data.image) {
                setBlog(prevBlog => ({...prevBlog, image: response.data.image}));
            } else if (postId) {
                // If updating and no new image path in response, re-fetch
                fetchUpdatedPost(postId);
            }
        } catch (error) {
            setMessage({type: "error", text: "err: " + (error.response?.data?.error || error.message)});
        }
    };

    const fetchUpdatedPost = async (id) => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/blog/draft/${id}`, {
                headers: {
                    'csrf-token': token,
                },
                withCredentials: true
            });

            if (res.data && res.data.draft) {
                const postData = res.data.draft;
                // --- SAFE TAGS PARSING ---
                let cleanTags = [];
                if (postData.tags) {
                    if (Array.isArray(postData.tags)) {
                        cleanTags = postData.tags;
                    } else if (typeof postData.tags === 'string') {
                        try {
                            // Try parsing in case it's a JSON string '["tag1", "tag2"]'
                            cleanTags = JSON.parse(postData.tags);
                        } catch (e) {
                            // Fallback if it's a comma-separated string "tag1,tag2"
                            cleanTags = postData.tags.split(',').map(t => t.trim()).filter(Boolean);
                        }
                    }
                }

                // Set the blog state with the parsed tags array
                const updatedBlog = {
                    ...postData,
                    tags: cleanTags
                };

                setBlog(updatedBlog);
            }
        } catch (error) {
        }
    };

    return (
        <div className="flex flex-1">
            <div className="flex flex-1">
                <div className='w-1/5'>
                    <AdminMenu path='./'/>
                </div>
                <div className='flex-grow relative w-4/5'>
                    <AdminHeader title='مقاله جدید'/>
                    {message.text && <ErrorMessage type={message.type} message={message.text}/>}
                    <form encType='multipart/form-data' className='px-8 py-5' onSubmit={(e) => e.preventDefault()}>
                        <div className='flex justify-end'>
                            <Button className='p-1 bg-light-primary text-white'
                                    onClick={() => saveOrUpdateBlog(status ? 1 : 0)}>
                                <FontAwesomeIcon icon={faSave} className='h-4'/>
                                <span>ذخیره</span>
                            </Button>
                        </div>
                        <div className='w-1/2 mb-10'>
                            <InputField name='title' title='تیتر' type='text' value={blog.title || ''}
                                        onChange={handleInputChange}/>
                        </div>
                        <div className='w-1/2 mb-10'>
                            <InputField name='h1' title='هدینگ ۱' type='text' value={blog.h1}
                                        onChange={handleInputChange}/>
                        </div>
                        <div className='w-1/2 mb-10'>
                            <InputField name='slug' title='نامک' type='text' value={blog.slug || ''}
                                        onChange={handleTitleChange}/>
                        </div>
                        <div className='w-1/2 mb-10'>
                            <InputField name='category' title='دسته‌بندی' type='text' value={blog.category || ''}
                                        onChange={handleTitleChange}/>
                        </div>
                        <label className='text-gray-500 text-md font-bold mb-3'>عکس اصلی</label>
                        <ImageUpload
                            name="image"
                            existingImage={resolvePublicImageUrl(blog.image)}
                            onFileChange={handleFileUpload}
                        />


                        <TextEditor content={blog.content} onContentChange={handleEditorContentChange}/>

                        <TextArea name='meta_description' title='توضیحات متا' type='text'
                                  value={blog.meta_description || ''}
                                  onChange={handleInputChange}/>

                        <TagsInput name='tags' title='تگ ها' type='text' value={blog.tags || ''}
                                   onChange={handleInputChange}/>

                        <div className='flex justify-end'>
                            <Button className='p-1 bg-light-primary m-3 text-white'
                                    onClick={() => saveOrUpdateBlog(1)}>
                                <span>{status ? "بروزرسانی" : "انتشار"}</span>
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminBlogPost;